import { Button } from '@ics-portal/guardian';
import { type ReactNode, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
    NewComponentRequestForm,
    type NewComponentRequestFormValue,
} from '@components/NewComponentCreateRequestTrigger/components/NewComponentsRequestDialog/components/NewComponentRequestForm';
import { ComponentCreateRequestRequestPriority } from '@gql/graphql';
import { useNewComponentRequestActions } from '@hooks/NewComponentRequests/useNewComponentRequestActions';
import { type RequestProps } from '../../models/RequestProps';
import s from './EditRequestContent.module.scss';

interface EditRequestContentProps extends RequestProps {
    disableEditMode: () => void;
}

export function EditRequestContent({
    request,
    disableEditMode,
}: EditRequestContentProps): ReactNode {
    const { update } = useNewComponentRequestActions();

    const form = useForm<NewComponentRequestFormValue>({
        defaultValues: {
            id: request.id,
            componentVendor: request.componentVendor
                ? {
                      label: request.componentVendor.name ?? '',
                      value: request.componentVendor.id ?? '',
                  }
                : request.componentVendorName || '',
            componentName: request.componentName || '',
            componentVersion: request.componentVersion || '',
            requestPriority:
                request.requestPriority ??
                ComponentCreateRequestRequestPriority.Low,
            referenceWeb: request.referenceWeb || '',
            referenceSecurity: request.referenceSecurity || '',
        },
    });

    const onSubmit = useCallback(
        async (data: Partial<NewComponentRequestFormValue>) => {
            await update(data);

            disableEditMode();
        },
        [update],
    );

    return (
        <form
            id="newComponentRequestForm"
            className="flex flex-column justify-content-between h-full"
            onSubmit={form.handleSubmit(onSubmit)}
        >
            <NewComponentRequestForm compactVersion form={form} />
            <div className={s.buttonWrapper}>
                <Button label="Cancel" outlined onClick={disableEditMode} />
                <Button
                    label="Save"
                    type="submit"
                    form="newComponentRequestForm"
                />
            </div>
        </form>
    );
}
